<template>
    <div class="grid items-end" :style="gridStyle">
        <div class="flex items-center gap-2 px-4 pt-8 pb-3 text-sm font-semibold text-body">
            {{ trans('dashboard.v2.preventative_maintenance.timeline.service_provider') }}
        </div>
        <button
            type="button"
            class="flex h-9 w-9 items-center justify-center self-end justify-self-center rounded-full text-muted transition hover:bg-gray-100 disabled:opacity-30"
            :disabled="!canStepLeft"
            :aria-label="trans('dashboard.v2.preventative_maintenance.timeline.previous')"
            @click="$emit('step-left')"
        >
            <span aria-hidden="true">&lsaquo;</span>
        </button>
        <div
            v-for="(year, index) in years"
            :key="year"
            class="relative flex flex-col items-center justify-end pt-8 pb-3 text-sm"
            :class="[year === currentYear ? 'font-semibold text-primary' : 'text-body', index > 0 ? 'border-l border-gray-200' : '']"
        >
            <!-- Today paged out of view: pin a clickable chip to the edge it lies beyond. -->
            <button
                v-if="todayBeyond === 'left' && index === 0"
                type="button"
                class="absolute top-0 left-0 z-10 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-white transition hover:bg-primary/90"
                :aria-label="trans('dashboard.v2.preventative_maintenance.timeline.jump_to_today')"
                @click="$emit('jump-to-today')"
            >
                <span aria-hidden="true">&lsaquo;</span> {{ trans('dashboard.v2.preventative_maintenance.timeline.today') }}
            </button>
            <button
                v-else-if="todayBeyond === 'right' && index === years.length - 1"
                type="button"
                class="absolute top-0 right-0 z-10 flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-white transition hover:bg-primary/90"
                :aria-label="trans('dashboard.v2.preventative_maintenance.timeline.jump_to_today')"
                @click="$emit('jump-to-today')"
            >
                {{ trans('dashboard.v2.preventative_maintenance.timeline.today') }} <span aria-hidden="true">&rsaquo;</span>
            </button>

            <template v-if="year === currentYear">
                <button
                    type="button"
                    class="absolute top-0 -translate-x-1/2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap text-white transition hover:bg-primary/90"
                    :style="{ left: `${todayLeftPercent}%` }"
                    :aria-label="trans('dashboard.v2.preventative_maintenance.timeline.jump_to_today')"
                    @click="$emit('jump-to-today')"
                >
                    {{ trans('dashboard.v2.preventative_maintenance.timeline.today') }} · {{ todayDateLabel }}
                </button>
                <span
                    class="pointer-events-none absolute top-4 bottom-0 w-px bg-primary/70"
                    :style="{ left: `calc(${todayLeftPercent}% - 0.5px)` }"
                    aria-hidden="true"
                />
            </template>
            {{ year }}
        </div>
        <button
            type="button"
            class="flex h-9 w-9 items-center justify-center self-end justify-self-center rounded-full text-muted transition hover:bg-gray-100 disabled:opacity-30"
            :disabled="!canStepRight"
            :aria-label="trans('dashboard.v2.preventative_maintenance.timeline.next')"
            @click="$emit('step-right')"
        >
            <span aria-hidden="true">&rsaquo;</span>
        </button>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { DateTime } from 'luxon';
import { usePage } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n';
import { headerGridTemplate } from './timelineLayout';

const props = defineProps({
    years: {
        type: Array,
        required: true,
    },
    currentYear: {
        type: Number,
        required: true,
    },
    canStepLeft: {
        type: Boolean,
        default: true,
    },
    canStepRight: {
        type: Boolean,
        default: true,
    },
});

defineEmits(['step-left', 'step-right', 'jump-to-today']);

const gridStyle = computed(() => ({
    gridTemplateColumns: headerGridTemplate(props.years.length),
}));

// Which side the current year lies beyond when it's scrolled out of the
// visible window (years are ascending left→right): 'left' when we've paged
// into the future, 'right' when into the past, null when today is visible.
const todayBeyond = computed(() => {
    if (props.years.length === 0) {
        return null;
    }
    if (props.currentYear < props.years[0]) {
        return 'left';
    }
    if (props.currentYear > props.years[props.years.length - 1]) {
        return 'right';
    }
    return null;
});

const todayLeftPercent = computed(() => {
    const now = DateTime.now();
    const startOfYear = DateTime.fromObject({ year: now.year, month: 1, day: 1 });
    const endOfYear = DateTime.fromObject({ year: now.year + 1, month: 1, day: 1 });
    const fraction = (now.toMillis() - startOfYear.toMillis()) / (endOfYear.toMillis() - startOfYear.toMillis());
    return Math.max(0, Math.min(1, fraction)) * 100;
});

const page = usePage();
const todayDateLabel = computed(() => {
    // Locale-aware short month + day (e.g. "May 25" / "25 mai" / "٢٥ مايو").
    const formatter = new Intl.DateTimeFormat(page.props.locale ?? 'en', { month: 'short', day: 'numeric' });
    return formatter.format(new Date());
});
</script>
